import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
} from "docx";
import { preprocessMarkdown } from "../utils/markdown-preprocessor";
import { demoteRawHtmlForWord } from "../utils/demote-raw-html";
import { DEFAULT_SETUP, pageGeometry, type DocumentSetup } from "../document-setup";
import { downloadBlob } from "../utils/download-blob";

export async function convertMarkdownToDocx(markdown: string, setup: DocumentSetup = DEFAULT_SETUP): Promise<Blob> {
  const geo = pageGeometry(setup);
  // Normalize formulas, and demote raw HTML (callout divs, <strong>, <br>…)
  // to markdown — this line-based converter would otherwise leak the tags as
  // literal text (issue #21).
  const processedMarkdown = preprocessMarkdown(demoteRawHtmlForWord(markdown));

  // NOTE: This client-side converter provides basic LaTeX rendering as styled text.
  // For production-quality math equations with proper OMML (Office Math) formatting,
  // use the Pandoc API endpoint which converts LaTeX to native Word equations via MathML.

  // Parse markdown into AST
  const lines = processedMarkdown.split("\n");
  const children: (Paragraph | Table)[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Block formulas $$...$$
    if (line.trim() === "$$") {
      const formulaLines: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== "$$") {
        formulaLines.push(lines[i]);
        i++;
      }

      // Clean up the formula text
      const formulaText = formulaLines.join("\n").trim();
      if (formulaText) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: formulaText,
                font: "Cambria Math",
                italics: true,
                size: 22,
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 240, after: 240 },
          })
        );
      }
      i++;
      continue;
    }

    // Headers
    if (line.startsWith("# ")) {
      children.push(
        new Paragraph({
          text: line.substring(2),
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 240, after: 120 },
        })
      );
      i++;
      continue;
    } else if (line.startsWith("## ")) {
      children.push(
        new Paragraph({
          text: line.substring(3),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 240, after: 120 },
        })
      );
      i++;
      continue;
    } else if (line.startsWith("### ")) {
      children.push(
        new Paragraph({
          text: line.substring(4),
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 },
        })
      );
      i++;
      continue;
    } else if (line.startsWith("#### ")) {
      children.push(
        new Paragraph({
          text: line.substring(5),
          heading: HeadingLevel.HEADING_4,
          spacing: { before: 160, after: 80 },
        })
      );
      i++;
      continue;
    } else if (line.startsWith("##### ")) {
      children.push(
        new Paragraph({
          text: line.substring(6),
          heading: HeadingLevel.HEADING_5,
          spacing: { before: 140, after: 70 },
        })
      );
      i++;
      continue;
    } else if (line.startsWith("###### ")) {
      children.push(
        new Paragraph({
          text: line.substring(7),
          heading: HeadingLevel.HEADING_6,
          spacing: { before: 120, after: 60 },
        })
      );
      i++;
      continue;
    }

    // Horizontal rule
    if (line.trim() === "---" || line.trim() === "***" || line.trim() === "___") {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "─".repeat(50),
              color: "808080",
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { before: 240, after: 240 },
        })
      );
      i++;
      continue;
    }

    // Code blocks
    if (line.startsWith("```")) {
      const codeLines: string[] = [];
      const language = line.substring(3).trim();
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }

      if (codeLines.length > 0) {
        // Add language label if specified
        if (language) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `[${language}]`,
                  color: "666666",
                  size: 18,
                }),
              ],
              spacing: { before: 120, after: 0 },
            })
          );
        }

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: codeLines.join("\n"),
                font: "Courier New",
                size: 20,
              }),
            ],
            shading: {
              fill: "FAF9F5",
            },
            spacing: { before: language ? 0 : 120, after: 120 },
            indent: { left: 360 },
          })
        );
      }
      i++;
      continue;
    }

    // Blockquotes
    if (line.startsWith("> ")) {
      const quoteText = line.substring(2);
      const textRuns = parseInlineFormatting(quoteText);
      children.push(
        new Paragraph({
          children: textRuns,
          spacing: { before: 120, after: 120 },
          indent: { left: 720 },
          shading: { fill: "F0F0F0" },
        })
      );
      i++;
      continue;
    }

    // Tables — a row of cells followed by a separator row (GFM). Works with or
    // without leading/trailing pipes.
    if (line.includes("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
        tableLines.push(lines[i]);
        i++;
      }
      const table = buildTable(tableLines);
      if (table) {
        children.push(table);
        continue;
      }
      // Fall through if the table couldn't be built (shouldn't normally happen).
    }

    // Bullet lists
    if (line.startsWith("- ") || line.startsWith("* ") || line.startsWith("+ ")) {
      const listText = line.substring(2);
      const textRuns = parseInlineFormatting(listText);
      children.push(
        new Paragraph({
          children: textRuns,
          bullet: {
            level: 0,
          },
          spacing: { before: 60, after: 60 },
        })
      );
      i++;
      continue;
    }

    // Numbered lists
    if (/^\d+\.\s/.test(line)) {
      const listText = line.replace(/^\d+\.\s/, "");
      const textRuns = parseInlineFormatting(listText);
      children.push(
        new Paragraph({
          children: textRuns,
          numbering: {
            reference: "default-numbering",
            level: 0,
          },
          spacing: { before: 60, after: 60 },
        })
      );
      i++;
      continue;
    }

    // Regular paragraphs
    if (line.trim()) {
      // Handle inline formatting
      const textRuns = parseInlineFormatting(line);
      children.push(
        new Paragraph({
          children: textRuns,
          spacing: { before: 120, after: 120 },
        })
      );
    } else if (i > 0 && i < lines.length - 1) {
      // Empty line (skip if at beginning or end)
      children.push(new Paragraph({ text: "" }));
    }

    i++;
  }

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            size: { width: geo.pageWidthTwips, height: geo.pageHeightTwips },
            margin: { top: geo.marginTwips, right: geo.marginTwips, bottom: geo.marginTwips, left: geo.marginTwips },
          },
        },
        children: children,
      },
    ],
    numbering: {
      config: [
        {
          reference: "default-numbering",
          levels: [
            {
              level: 0,
              format: "decimal",
              text: "%1.",
              alignment: AlignmentType.START,
            },
          ],
        },
      ],
    },
  });

  const blob = await Packer.toBlob(doc);
  return blob;
}

function parseInlineFormatting(text: string): TextRun[] {
  const runs: TextRun[] = [];
  let currentText = "";
  let i = 0;

  while (i < text.length) {
    // Check for escaped characters
    if (text[i] === "\\" && i + 1 < text.length) {
      // Handle escaped characters
      currentText += text[i + 1];
      i += 2;
      continue;
    }

    // Inline math $formula$
    if (text[i] === "$" && (i === 0 || text[i - 1] !== "\\")) {
      // Check if this is actually a math formula start
      let j = i + 1;
      let foundClosing = false;
      while (j < text.length) {
        if (text[j] === "$" && text[j - 1] !== "\\") {
          foundClosing = true;
          break;
        }
        j++;
      }

      if (foundClosing) {
        if (currentText) {
          runs.push(new TextRun({ text: currentText }));
          currentText = "";
        }
        i++;
        let mathText = "";
        while (i < text.length && !(text[i] === "$" && text[i - 1] !== "\\")) {
          mathText += text[i];
          i++;
        }

        // Clean up the math text
        mathText = mathText.trim();
        if (mathText) {
          runs.push(
            new TextRun({
              text: mathText,
              font: "Cambria Math",
              italics: true,
            })
          );
        }
        i++;
        continue;
      }
    }

    // Bold **text** or __text__
    if ((text.substring(i, i + 2) === "**" || text.substring(i, i + 2) === "__") &&
        text[i - 1] !== "\\") {
      const marker = text.substring(i, i + 2);
      let j = i + 2;
      let foundClosing = false;
      while (j < text.length - 1) {
        if (text.substring(j, j + 2) === marker && text[j - 1] !== "\\") {
          foundClosing = true;
          break;
        }
        j++;
      }

      if (foundClosing) {
        if (currentText) {
          runs.push(new TextRun({ text: currentText }));
          currentText = "";
        }
        i += 2;
        let boldText = "";
        while (i < text.length && text.substring(i, i + 2) !== marker) {
          boldText += text[i];
          i++;
        }

        // Parse nested formatting within bold text
        const nestedRuns = parseNestedFormatting(boldText, { bold: true });
        runs.push(...nestedRuns);
        i += 2;
        continue;
      }
    }

    // Italic *text* or _text_
    if ((text[i] === "*" || text[i] === "_") &&
        text[i + 1] !== text[i] &&
        (i === 0 || text[i - 1] !== "\\")) {
      const marker = text[i];
      let j = i + 1;
      let foundClosing = false;
      while (j < text.length) {
        if (text[j] === marker && text[j - 1] !== "\\" && text[j + 1] !== marker) {
          foundClosing = true;
          break;
        }
        j++;
      }

      if (foundClosing) {
        if (currentText) {
          runs.push(new TextRun({ text: currentText }));
          currentText = "";
        }
        i++;
        let italicText = "";
        while (i < text.length && text[i] !== marker) {
          italicText += text[i];
          i++;
        }

        // Parse nested formatting within italic text
        const nestedRuns = parseNestedFormatting(italicText, { italics: true });
        runs.push(...nestedRuns);
        i++;
        continue;
      }
    }

    // Inline code `code`
    if (text[i] === "`" && (i === 0 || text[i - 1] !== "\\")) {
      let j = i + 1;
      let foundClosing = false;
      while (j < text.length) {
        if (text[j] === "`" && text[j - 1] !== "\\") {
          foundClosing = true;
          break;
        }
        j++;
      }

      if (foundClosing) {
        if (currentText) {
          runs.push(new TextRun({ text: currentText }));
          currentText = "";
        }
        i++;
        let codeText = "";
        while (i < text.length && text[i] !== "`") {
          codeText += text[i];
          i++;
        }
        runs.push(
          new TextRun({
            text: codeText,
            font: "Courier New",
            shading: { fill: "FAF9F5" },
          })
        );
        i++;
        continue;
      }
    }

    currentText += text[i];
    i++;
  }

  if (currentText) {
    runs.push(new TextRun({ text: currentText }));
  }

  return runs.length > 0 ? runs : [new TextRun({ text: "" })];
}

// Helper function to parse nested formatting
function parseNestedFormatting(text: string, baseStyle: Record<string, unknown>): TextRun[] {
  // For simplicity, return a single run with the base style
  // You could enhance this to handle nested formatting if needed
  return [new TextRun({ text, ...baseStyle })];
}

// ── GFM table support ──────────────────────────────────────────────────────

const TABLE_BORDER = { style: BorderStyle.SINGLE, size: 1, color: "BFBFBF" } as const;

/** A GFM delimiter row: dashes, optional colons, pipes — and at least one of each. */
function isTableSeparator(line: string): boolean {
  const t = line.trim();
  return t.includes("-") && t.includes("|") && /^[\s|:-]+$/.test(t);
}

/** Split a table row into trimmed cells, honoring escaped pipes (\|). */
function splitTableCells(line: string): string[] {
  const PH = "";
  const cells = line
    .replace(/\\\|/g, PH)
    .split("|")
    .map((c) => c.replace(new RegExp(PH, "g"), "\\|").trim());
  if (cells.length && cells[0] === "") cells.shift();
  if (cells.length && cells[cells.length - 1] === "") cells.pop();
  return cells;
}

function alignmentFromSpec(spec: string): (typeof AlignmentType)[keyof typeof AlignmentType] | undefined {
  const s = spec.trim();
  const left = s.startsWith(":");
  const right = s.endsWith(":");
  if (left && right) return AlignmentType.CENTER;
  if (right) return AlignmentType.RIGHT;
  if (left) return AlignmentType.LEFT;
  return undefined;
}

/** Strip the common inline markdown markers for header cells (rendered bold). */
function stripInlineMd(text: string): string {
  return text
    .replace(/\*\*/g, "")
    .replace(/[*_`]/g, "")
    .replace(/\\([\\|`*_$])/g, "$1")
    .trim();
}

function buildRow(
  cells: string[],
  aligns: ReturnType<typeof alignmentFromSpec>[],
  colCount: number,
  header: boolean,
): TableRow {
  const tableCells: TableCell[] = [];
  for (let c = 0; c < colCount; c++) {
    const text = cells[c] ?? "";
    const alignment = aligns[c];
    const runs = header
      ? [new TextRun({ text: stripInlineMd(text), bold: true })]
      : parseInlineFormatting(text);
    tableCells.push(
      new TableCell({
        children: [
          new Paragraph({
            children: runs.length ? runs : [new TextRun({ text: "" })],
            alignment,
          }),
        ],
        shading: header ? { fill: "F2F2F2" } : undefined,
      }),
    );
  }
  return new TableRow({ children: tableCells });
}

/** Build a real Word table (w:tbl) from GFM table lines, or null if malformed. */
function buildTable(tableLines: string[]): Table | null {
  if (tableLines.length < 2 || !isTableSeparator(tableLines[1])) return null;

  const header = splitTableCells(tableLines[0]);
  if (header.length === 0) return null;

  const aligns = splitTableCells(tableLines[1]).map(alignmentFromSpec);
  const colCount = header.length;

  // An all-empty header row is how a headerless table is written (see
  // rehype-drop-empty-header.ts): leave it out rather than draw a blank row.
  const rows: TableRow[] = header.every((c) => !c.trim()) ? [] : [buildRow(header, aligns, colCount, true)];
  for (const bodyLine of tableLines.slice(2)) {
    rows.push(buildRow(splitTableCells(bodyLine), aligns, colCount, false));
  }
  if (rows.length === 0) return null;

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: TABLE_BORDER,
      bottom: TABLE_BORDER,
      left: TABLE_BORDER,
      right: TABLE_BORDER,
      insideHorizontal: TABLE_BORDER,
      insideVertical: TABLE_BORDER,
    },
    rows,
  });
}

export function downloadDocx(blob: Blob, filename: string = "document.docx") {
  downloadBlob(blob, filename);
}